var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable react/require-default-props */
import React, { useState, useCallback, useMemo } from 'react';
import { translate } from '@utils/i18n';
import Dropdown from '@components/v2/Dropdown';
import CrudHeader from '@components/v2/CrudHeader';
import TabView from '@components/TabView';
import { Button } from '@components/v2/Button';
import Tags from '@components/Tags';
import ActivityFeed from '@features/ActivityFeed';
import PhotoGallery from '@features/PhotoGallery';
import Loader, { LoaderSizes } from '@components/v2/Loader';
import toast from 'react-hot-toast';
import ActionButtons from './components/ActionButtons';
import { namespace } from './constants';
import DetailInfo from './components/DetailInfo/DetailInfo';
import DeleteModal from './components/DeleteModal';
import ResolveModal from '../components/ResolveModal/ResolveModal';
import useUpdateDeficiencyDetail from '../hooks/useUpdateDeficiency';
import useDeficiencyDetail from '../hooks/useDeficiencyDetail';
function DeficiencyDetail({ deficiencyId, deficiencyStatuses, deficiencyResolutionStatuses, tags }) {
    var _a, _b;
    const { data: deficiency, isLoading, error } = useDeficiencyDetail(deficiencyId);
    const [resolveDialogVisible, setResolveDialogVisible] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const toggleResolveDialog = useCallback(() => setResolveDialogVisible((prev) => !prev), []);
    const toggleDeleteDialog = useCallback(() => setDeleteDialogVisible((prev) => !prev), []);
    const { updateDeficiency } = useUpdateDeficiencyDetail();
    const tagsForSelect = useMemo(() => tags.map(({ label, value }) => ({
        label,
        value: Number(value)
    })), [tags]);
    const onUnresolve = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            yield updateDeficiency.mutateAsync({ id: deficiencyId, deficiencyUpdate: { isResolved: false } });
            toast.success('Deficiency reopened successfully');
        }
        catch (_a) {
            toast.error(translate('error_message_updating', { namespace }));
        }
    }), [updateDeficiency, deficiencyId]);
    const onChangeStatus = useCallback((value) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield updateDeficiency.mutateAsync({ id: deficiencyId, deficiencyUpdate: { deficiencyStatus: value } });
            toast.success('Deficiency status updated successfully');
        }
        catch (_a) {
            toast.error(translate('error_message_updating', { namespace }));
        }
    }), [updateDeficiency, deficiencyId]);
    const onChangeResolutionStatus = useCallback((value) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield updateDeficiency.mutateAsync({
                id: deficiencyId,
                deficiencyUpdate: { deficiencyResolutionStatus: Number(value) }
            });
            toast.success('Deficiency resolution status updated successfully');
        }
        catch (_a) {
            toast.error(translate('error_message_updating', { namespace }));
        }
    }), [updateDeficiency, deficiencyId]);
    const tabs = [
        {
            id: 'details',
            label: translate('tabs.details', { namespace }),
            content: React.createElement(DetailInfo, { id: deficiencyId })
        },
        {
            id: 'activity',
            label: translate('tabs.activity', { namespace }),
            content: (React.createElement(ActivityFeed, { notesEnabled: false, buildingId: (_a = deficiency === null || deficiency === void 0 ? void 0 : deficiency.building.id) !== null && _a !== void 0 ? _a : 0, deficiencyId: Number(deficiencyId) }))
        },
        {
            id: 'photos',
            label: translate('tabs.photos', { namespace }),
            content: React.createElement(PhotoGallery, { deficiencyId: Number(deficiencyId) })
        }
    ];
    if (isLoading) {
        return (React.createElement("div", { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }, className: "msgContainer" },
            React.createElement(Loader, { size: LoaderSizes.xlarge })));
    }
    if (error || !deficiency) {
        return React.createElement("div", { className: "msgContainer" }, translate('def_error_message', { namespace }));
    }
    return (React.createElement("div", { className: "main-content" },
        React.createElement("div", { className: "page-content" },
            React.createElement(CrudHeader, { title: `${translate('title', { namespace })} #${deficiency.id}` },
                React.createElement(ActionButtons, null,
                    React.createElement(Button, { onClick: toggleDeleteDialog },
                        React.createElement("i", { className: "fa fa-trash" }),
                        " ",
                        translate('delete', { namespace: 'generic' })),
                    deficiency.isResolved ? (React.createElement(Button, { variant: "secondary", onClick: () => {
                            void onUnresolve();
                        } },
                        React.createElement("i", { className: "fa fa-rotate-left" }),
                        " ",
                        translate('reopen', { namespace }))) : (React.createElement(Button, { variant: "secondary", onClick: toggleResolveDialog },
                        React.createElement("i", { className: "fa fa-check" }),
                        " ",
                        translate('resolve', { namespace }))))),
            React.createElement("div", { className: "row-fluid d-flex", style: { gap: '1.6rem' } },
                React.createElement("div", { className: "form-group" },
                    React.createElement(Dropdown, { resourceOptions: deficiencyStatuses, resourceName: translate('status', { namespace }), selectedItem: deficiency.deficiencyStatus, showLabel: true, inlineLabel: true, allowEmpty: false, onChange: (value) => {
                            if (!value)
                                return;
                            void onChangeStatus(value);
                        } })),
                React.createElement("div", { className: "form-group" },
                    React.createElement(Dropdown, { resourceOptions: deficiencyResolutionStatuses, resourceName: translate('resolution', { namespace }), selectedItem: ((_b = deficiency.deficiencyResolutionStatus) === null || _b === void 0 ? void 0 : _b.toString()) || null, showLabel: true, inlineLabel: true, allowEmpty: false, onChange: (value) => {
                            if (!value)
                                return;
                            void onChangeResolutionStatus(value);
                        } })),
                React.createElement("div", { className: "form-group" },
                    React.createElement(Tags, { tagsList: deficiency.tagList, attributeName: "tag_list", tagsForSelect: tagsForSelect, onTagsChange: (values) => {
                            try {
                                void updateDeficiency.mutateAsync({ id: deficiencyId, deficiencyUpdate: { tagList: values } });
                            }
                            catch (_a) {
                                toast.error(translate('error_message_updating', { namespace }));
                            }
                        } }))),
            React.createElement(TabView, { tabs: tabs }),
            deleteDialogVisible && React.createElement(DeleteModal, { deficiencyId: deficiency.id, toggleDialog: toggleDeleteDialog }),
            resolveDialogVisible && React.createElement(ResolveModal, { toggleDialog: toggleResolveDialog, deficiencyId: deficiency.id }))));
}
export default DeficiencyDetail;
